/**
 * The ONE frontend reader of the platform login-identifier mode.
 *
 * SPEC-USER-AUTH-RULE-017 (D-342): `system_settings.login_identifier_mode` is
 * `email` (the Keycloak username is derived by the gateway from the email;
 * nothing is typed as a login name) or `username` (a short operator-chosen
 * name, D-336's behaviour). The SPAs read it from the public
 * `GET /api/v1/features` (SPEC-GO-API-CONTRACT-035); no other module fetches
 * that resource (SPEC-FRONTEND-RULE-077: one key, one fetcher, one owner).
 *
 * DEFAULT_LOGIN_IDENTIFIER_MODE is the value shown while the request is in
 * flight or has failed. It MIRRORS the Go `DefaultSystemSettings()` value in
 * internal/repository/settings.go, which is the single canonical default; it
 * is not a second definition of the convention (SPEC-FRONTEND-RULE-057's
 * one-default-source principle).
 *
 * @module utils/loginIdentifierMode
 * @see TASK-ADMIN-USER-CREATE-LOGIN-MODE-FE
 */
import { queryOptions, useQuery } from "@tanstack/react-query";
import client from "../api/client";

export type LoginIdentifierMode = "email" | "username";

/** Mirrors DefaultSystemSettings().LoginIdentifierMode (Go); loading/error fallback. */
export const DEFAULT_LOGIN_IDENTIFIER_MODE: LoginIdentifierMode = "email";

/** Shape of GET /api/v1/features (additive; missing keys have defaults). */
export interface PlatformFeatures {
  byok_credentials?: boolean;
  login_identifier_mode?: string;
  /**
   * The platform-wide word the TENANT SPA uses for an agent (D-368,
   * SPEC-GO-API-CONTRACT-035). Read through `utils/agentNoun.ts`, which owns
   * its narrowing and its display forms; a missing or unknown value is
   * "agents". It is declared here because this module owns the payload's
   * shape and its single fetcher (SPEC-FRONTEND-RULE-077) - agentNoun.ts
   * imports platformFeaturesQueryOptions() rather than opening a second one.
   */
  agent_noun_preset?: string;
  /**
   * The eight TYPED forms behind the `custom` preset (D-371,
   * SPEC-GO-API-CONTRACT-035 as amended). Present ONLY when
   * `agent_noun_preset` is `custom`: the gateway resolves both from one
   * record, so a half-filled vocabulary can never arrive beside a preset that
   * is not `custom`. Typed as `unknown` here on purpose - this module owns the
   * payload's shape and its one fetcher, while `utils/agentNoun.ts` owns the
   * narrowing, exactly as it does for the preset above.
   */
  agent_noun_custom?: unknown;
}

export function platformFeaturesQueryKey() {
  return ["platform", "features"] as const;
}

export async function fetchPlatformFeatures(): Promise<PlatformFeatures> {
  // The gateway serves /features with Cache-Control: no-store since it carries
  // a runtime setting; no-cache here defeats any intermediary that ignores it,
  // so a saved mode is what the next fetch returns (SPEC-GO-API-CONTRACT-035).
  const { data } = await client.get<PlatformFeatures>("/features", {
    headers: { "Cache-Control": "no-cache" },
  });
  return data ?? {};
}

export function platformFeaturesQueryOptions() {
  return queryOptions({
    queryKey: platformFeaturesQueryKey(),
    queryFn: fetchPlatformFeatures,
  });
}

/** Narrow an arbitrary payload value to a mode; anything else is the default. */
export function toLoginIdentifierMode(value: unknown): LoginIdentifierMode {
  return value === "username" ? "username" : DEFAULT_LOGIN_IDENTIFIER_MODE;
}

export interface LoginIdentifierModeState {
  /** The mode to render; DEFAULT_LOGIN_IDENTIFIER_MODE until resolved. */
  mode: LoginIdentifierMode;
  /** True once the gateway answered; a dialog keeps Create disabled until then. */
  isResolved: boolean;
  /** True when the fetch failed; a dialog shows a warning and keeps Create disabled. */
  isError: boolean;
}

/**
 * The effective platform mode plus its resolution state. `mode` is the default
 * while loading and on error - so a dialog never renders a login-name field
 * the gateway would ignore - but a consumer MUST gate its submit on
 * `isResolved`: silently creating under the wrong convention on a
 * username-mode deployment is exactly what the state exists to prevent.
 */
export function useLoginIdentifierMode(): LoginIdentifierModeState {
  const { data, isSuccess, isError } = useQuery({
    ...platformFeaturesQueryOptions(),
    select: (features) => toLoginIdentifierMode(features.login_identifier_mode),
  });
  return {
    mode: isSuccess && data ? data : DEFAULT_LOGIN_IDENTIFIER_MODE,
    isResolved: isSuccess,
    isError,
  };
}

/**
 * The ONE email-login charset (SPEC-USER-AUTH-RULE-017), applied after trim +
 * lowercase: exactly one "@", a non-empty local part and domain, only
 * lowercase letters, digits, ".", "_", "+", "-" (and "." / "-" in the domain).
 * A strict subset of what the realm's username-prohibited-characters validator
 * accepts (read live 2026-09-16), so the gateway never gets an address Keycloak
 * would refuse with a 400 naming a field this dialog does not show. Mirrors
 * validLoginEmail in services/api-gateway/routes/routes.go; the gateway is the
 * enforcement point, this is the early warning.
 */
const LOGIN_EMAIL_PATTERN = /^[a-z0-9._+-]+@[a-z0-9.-]+$/;

export function isValidLoginEmail(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v || v.length > 255) return false;
  for (const ch of Array.from(v)) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f || /\s/.test(ch)) return false;
  }
  return LOGIN_EMAIL_PATTERN.test(v);
}
